import React, { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { wyscoutMatchesAPI } from '@/services/api';
import { Props, Stats } from './MatchAdvancedStats/types';
import { SectionHeader } from './MatchAdvancedStats/SectionHeader';
import { TeamHeaders } from './MatchAdvancedStats/TeamHeaders';
import { discoverTeamIds } from './MatchAdvancedStats/discoverTeamIds';
import {
  GeneralSection,
  PossessionSection,
  PassingSection,
  DefenceSection,
  DuelsSection,
  AttacksSection,
  FlanksSection,
} from './MatchAdvancedStats/StatsSections';
import LoadingStatus from '@/components/ui/LoadingStatus';

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const hasObjectEntries = (value: unknown): value is Record<string, unknown> => (
  isRecord(value) && Object.values(value).every(isRecord)
);

const isStats = (value: unknown): value is Stats => {
  if (!isRecord(value)) return false;

  const sectionNames = ['general', 'passes', 'defence', 'duels', 'attacks', 'flanks'] as const;
  if (!sectionNames.every(section => hasObjectEntries(value[section]))) return false;
  if (!isRecord(value.possession) || !isRecord(value.teams)) return false;

  const possessionIsValid = Object.entries(value.possession).every(([key, entry]) => (
    key === 'totalTime' || key === 'deadTime' ? typeof entry === 'string' : isRecord(entry)
  ));
  if (!possessionIsValid) return false;

  return Object.values(value.teams).every(teamInfo => (
    isRecord(teamInfo)
    && typeof teamInfo.teamId === 'number'
    && Number.isFinite(teamInfo.teamId)
    && teamInfo.teamId > 0
    && (teamInfo.team === undefined || (
      isRecord(teamInfo.team)
      && typeof teamInfo.team.name === 'string'
      && (teamInfo.team.officialName === undefined || typeof teamInfo.team.officialName === 'string')
      && (teamInfo.team.imageDataURL === undefined || typeof teamInfo.team.imageDataURL === 'string')
    ))
  ));
};

const MatchAdvancedStats: React.FC<Props> = ({ matchId, homeTeamId, awayTeamId, homeTeamName, awayTeamName, statsData }) => {
  const controlled = statsData !== undefined;
  const [loading, setLoading] = useState(!controlled);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<'database' | 'api' | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    general: true,
    possession: true,
    passes: false,
    defence: false,
    duels: false,
    attacks: false,
    flanks: false,
  });

  useEffect(() => {
    if (controlled) return;

    let ignore = false;

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await wyscoutMatchesAPI.getMatchAdvancedStats(matchId);
        if (!ignore) {
          if (!isStats(response.data)) throw new Error('Invalid advanced stats response');
          setStats(response.data);
          setSource(response.source);
        }
      } catch (err: unknown) {
        if (!ignore) {
          const message = isAxiosError<{ error?: string }>(err)
            ? err.response?.data?.error || err.message
            : err instanceof Error ? err.message : null;
          setError(message || 'Failed to load advanced stats');
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    fetchStats();

    return () => {
      ignore = true;
    };
  }, [controlled, matchId]);

  const displayedStats = controlled ? statsData : stats;

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getTeamName = (teamId: number | string): string => {
    const id = String(teamId);
    const apiName = displayedStats?.teams?.[id]?.team?.name;
    if (apiName) return apiName;
    if (String(teamId) === String(homeTeamId) && homeTeamName) return homeTeamName;
    if (String(teamId) === String(awayTeamId) && awayTeamName) return awayTeamName;
    return 'Unknown team';
  };

  if (!controlled && loading) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Advanced Statistics</h3>
        <LoadingStatus label="Loading advanced match statistics" className="h-32 justify-center" />
      </div>
    );
  }

  if (!controlled && error) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow p-6 mt-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Advanced Statistics</h3>
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-800 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  if (!displayedStats) return null;

  const { homeId, awayId } = discoverTeamIds(displayedStats, homeTeamId, awayTeamId, homeTeamName);

  return (
    <div className={`bg-white dark:bg-gray-900 rounded-lg shadow ${controlled ? 'p-4 sm:p-6' : 'p-6 mt-6'}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Advanced Statistics</h3>
        {!controlled && source && (
          <span className={`text-xs px-2 py-1 rounded ${source === 'database' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'}`}>
            Source: {source === 'database' ? 'Cached' : 'API'}
          </span>
        )}
      </div>

      {/* Team Headers */}
      <TeamHeaders stats={displayedStats} homeId={homeId} awayId={awayId} getTeamName={getTeamName} />

      {/* General Stats */}
      <SectionHeader title="General" section="general" expanded={expandedSections.general} onToggle={toggleSection} />
      {expandedSections.general && displayedStats.general && (
        <GeneralSection stats={displayedStats} homeId={homeId} awayId={awayId} />
      )}

      {/* Possession Stats */}
      <SectionHeader title="Possession" section="possession" expanded={expandedSections.possession} onToggle={toggleSection} />
      {expandedSections.possession && displayedStats.possession && (
        <PossessionSection stats={displayedStats} homeId={homeId} awayId={awayId} />
      )}

      {/* Passing Stats */}
      <SectionHeader title="Passing" section="passes" expanded={expandedSections.passes} onToggle={toggleSection} />
      {expandedSections.passes && displayedStats.passes && (
        <PassingSection stats={displayedStats} homeId={homeId} awayId={awayId} />
      )}

      {/* Defence Stats */}
      <SectionHeader title="Defence" section="defence" expanded={expandedSections.defence} onToggle={toggleSection} />
      {expandedSections.defence && displayedStats.defence && (
        <DefenceSection stats={displayedStats} homeId={homeId} awayId={awayId} />
      )}

      {/* Duels Stats */}
      <SectionHeader title="Duels" section="duels" expanded={expandedSections.duels} onToggle={toggleSection} />
      {expandedSections.duels && displayedStats.duels && (
        <DuelsSection stats={displayedStats} homeId={homeId} awayId={awayId} />
      )}

      {/* Attack Stats */}
      <SectionHeader title="Attacks" section="attacks" expanded={expandedSections.attacks} onToggle={toggleSection} />
      {expandedSections.attacks && displayedStats.attacks && (
        <AttacksSection stats={displayedStats} homeId={homeId} awayId={awayId} />
      )}

      {/* Flanks Stats */}
      <SectionHeader title="Attack Zones" section="flanks" expanded={expandedSections.flanks} onToggle={toggleSection} />
      {expandedSections.flanks && displayedStats.flanks && (
        <FlanksSection stats={displayedStats} homeId={homeId} awayId={awayId} />
      )}
    </div>
  );
};

export default MatchAdvancedStats;
